import React from 'react';
import swal from 'sweetalert';
import { Spinner } from 'react-bootstrap';
import { Plus, Trash2, Search } from 'lucide-react';
import i18n from '../../../../../../i18n';
import { getAttributes, getAuctionAttributes, updateAuctionAttributes } from '../../../../../../models/attributes';

export class AttributesTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      auctionId: props.auctionId,
      categoryId: props.categoryId || null,
      availableAttributes: [],
      auctionAttributes: [
        { attribute_id: '', value: '' },
      ],
      is_loading: false,
      is_submitting: false,
      searchTerm: '',
    };
  }

  componentDidMount() {
    this.loadAvailableAttributes();
    this.loadAuctionAttributes();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.categoryId !== this.props.categoryId) {
      this.setState({ categoryId: this.props.categoryId }, () => {
        this.loadAvailableAttributes();
      });
    }
  }

  async loadAvailableAttributes() {
    try {
      const { categoryId } = this.state;
      let q = '';
      if (categoryId) {
        q = 'category=' + categoryId;
      }
      const response = await getAttributes(q);
      if (response && response.data) {
        this.setState({ availableAttributes: response.data });
      }
    } catch (err) {
      console.error('Error loading attributes:', err);
    }
  }

  async loadAuctionAttributes() {
    this.setState({ is_loading: true });
    try {
      const response = await getAuctionAttributes(this.state.auctionId);
      if (response && response.data && response.data.length) {
        const mapped = response.data.map((item) => ({
          attribute_id: item.attribute_id || item.id,
          value: item.value || '',
        }));
        this.setState({ auctionAttributes: mapped });
      }
    } catch (err) {
      console.error('Error loading auction attributes:', err);
    } finally {
      this.setState({ is_loading: false });
    }
  }

  handleAttributeChange = (index, field, val) => {
    const auctionAttributes = [...this.state.auctionAttributes];
    auctionAttributes[index][field] = val;
    this.setState({ auctionAttributes });
  };

  appendRow = (e) => {
    e.preventDefault();
    const auctionAttributes = [...this.state.auctionAttributes];
    auctionAttributes.push({ attribute_id: '', value: '' });
    this.setState({ auctionAttributes });
  };

  removeRow = (index) => {
    const auctionAttributes = [...this.state.auctionAttributes];
    if (auctionAttributes.length <= 1) {
      swal('Info', 'At least one attribute entry is required.', 'info');
      return;
    }
    auctionAttributes.splice(index, 1);
    this.setState({ auctionAttributes });
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const valid = this.state.auctionAttributes.filter(
      (a) => a.attribute_id && a.value.trim()
    );
    if (!valid.length) {
      swal('Failed', 'Please add at least one attribute with a value.', 'error');
      return;
    }

    this.setState({ is_submitting: true });
    try {
      const payload = {
        auction_id: this.state.auctionId,
        attributes: valid,
      };
      const response = await updateAuctionAttributes(payload);
      if (response && response.success) {
        swal(i18n.t('success'), response.message || 'Attributes updated', 'success', {
          buttons: false,
          timer: 2000,
        });
      } else {
        let error_message = response.message || 'Update failed';
        if (response.data) {
          for (const key in response.data) {
            if (Object.hasOwnProperty.call(response.data, key)) {
              error_message += '\n' + response.data[key];
            }
          }
        }
        swal('Failed', error_message, 'error');
      }
    } catch (err) {
      console.error(err);
      swal('Failed', 'An unexpected error occurred.', 'error');
    } finally {
      this.setState({ is_submitting: false });
    }
  };

  getAttributeMeta = (attrId) => {
    return this.state.availableAttributes.find(
      (a) => String(a.id) === String(attrId)
    );
  };

  getFilteredAttributes = () => {
    const { availableAttributes, searchTerm } = this.state;
    if (!searchTerm.trim()) return availableAttributes;
    const term = searchTerm.toLowerCase();
    return availableAttributes.filter(
      (a) => a.name.toLowerCase().includes(term)
    );
  };

  getUsedIds = () => {
    return this.state.auctionAttributes
      .map((a) => String(a.attribute_id))
      .filter(Boolean);
  };

  render() {
    const { auctionAttributes, availableAttributes, is_loading, is_submitting, categoryId } = this.state;
    const usedIds = this.getUsedIds();

    return (
      <form onSubmit={this.handleSubmit}>
        <div className="form-section">
          <div className="section-header">
            <h2>{i18n.t('attributes') || 'Attributes'}</h2>
            <button
              type="button"
              className="button1 button1-secondary"
              onClick={this.appendRow}
            >
              <Plus size={18} /> {i18n.t('add_new') || 'Add New'}
            </button>
          </div>

          {/* Category info badge */}
          {categoryId && (
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              Showing attributes for this auction's category.
              Select an attribute and provide its value.
            </p>
          )}

          {is_loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner animation="border" />
            </div>
          )}

          {!is_loading && availableAttributes.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#9ca3af',
                background: '#fafafa',
                borderRadius: '8px',
              }}
            >
              <p>No attributes available for this auction's category.</p>
              <p style={{ fontSize: '12px' }}>
                Create attributes for this category from the{' '}
                <a href="/attributes/create" style={{ color: '#10b981' }}>
                  Attributes page
                </a>.
              </p>
            </div>
          )}

          {!is_loading && availableAttributes.length > 0 && auctionAttributes.length > 0 && (
            <>
              {auctionAttributes.map((row, index) => {
                const meta = this.getAttributeMeta(row.attribute_id);
                return (
                  <div key={index} className="enquiry-item">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {meta && meta.icon && (
                          <i
                            className={`fa ${meta.icon}`}
                            style={{ color: '#10b981', fontSize: '16px' }}
                          />
                        )}
                        {meta ? meta.name : `Attribute #${index + 1}`}
                      </span>
                      {auctionAttributes.length > 1 && (
                        <button
                          type="button"
                          className="button1 button1-danger"
                          style={{ padding: '4px 10px', fontSize: '12px' }}
                          onClick={() => this.removeRow(index)}
                        >
                          <Trash2 size={14} /> {i18n.t('remove') || 'Remove'}
                        </button>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label>{i18n.t('attribute') || 'Attribute'} *</label>
                        <select
                          value={row.attribute_id}
                          onChange={(e) =>
                            this.handleAttributeChange(index, 'attribute_id', e.target.value)
                          }
                        >
                          <option value="">
                            -- {i18n.t('select_attribute') || 'Select Attribute'} --
                          </option>
                          {availableAttributes.map((attr) => (
                            <option
                              key={attr.id}
                              value={attr.id}
                              disabled={
                                usedIds.includes(String(attr.id)) &&
                                String(attr.id) !== String(row.attribute_id)
                              }
                            >
                              {attr.name}
                              {attr.name_ar ? ` - ${attr.category_relation?.category_name}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-field">
                        <label>{i18n.t('value') || 'Value'} *</label>
                        <input
                          type="text"
                          placeholder={
                            meta
                              ? `e.g. ${meta.name === 'Area Size' ? '600 m²' : meta.name === 'Mileage' ? '45,000 km' : 'Enter value'}`
                              : 'Enter value'
                          }
                          value={row.value}
                          onChange={(e) =>
                            this.handleAttributeChange(index, 'value', e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Quick-add chips */}
        {availableAttributes.length > 0 && (
          <div className="form-section">
            <h2>{i18n.t('available_attributes') || 'Available Attributes'}</h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Click to add quickly.
            </p>

            <div className="form-field form-field-margin-bottom">
              <div style={{ position: 'relative' }}>
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                  }}
                />
                <input
                  type="text"
                  placeholder="Search attributes..."
                  value={this.state.searchTerm}
                  onChange={(e) => this.setState({ searchTerm: e.target.value })}
                  style={{ paddingLeft: '36px' }}
                />
              </div>
            </div>

            <div className="payment-type-group">
              {this.getFilteredAttributes().map((attr) => {
                const isUsed = usedIds.includes(String(attr.id));
                return (
                  <label
                    key={attr.id}
                    className="payment-checkbox"
                    style={{
                      opacity: isUsed ? 0.5 : 1,
                      cursor: isUsed ? 'default' : 'pointer',
                    }}
                    onClick={() => {
                      if (isUsed) return;
                      const attrs = [...this.state.auctionAttributes];
                      const emptyIdx = attrs.findIndex((a) => !a.attribute_id);
                      if (emptyIdx >= 0) {
                        attrs[emptyIdx].attribute_id = attr.id;
                        this.setState({ auctionAttributes: attrs });
                      } else {
                        attrs.push({ attribute_id: attr.id, value: '' });
                        this.setState({ auctionAttributes: attrs });
                      }
                    }}
                  >
                    {attr.icon && (
                      <i
                        className={`fa ${attr.icon}`}
                        style={{ fontSize: '14px', color: isUsed ? '#9ca3af' : '#10b981' }}
                      />
                    )}
                    <span>{attr.name}</span>
                    {isUsed && (
                      <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>✓</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="action-buttons-l2 border-top-light">
          <button
            type="button"
            className="button1 button1-gray"
            onClick={() => window.history.back()}
            disabled={is_submitting}
          >
            {i18n.t('cancel') || 'Cancel'}
          </button>
          <button
            type="submit"
            className="button1 button1-primary"
            disabled={is_submitting}
          >
            {is_submitting
              ? i18n.t('saving') || 'Saving...'
              : i18n.t('update') || 'Update Attributes'}
          </button>
        </div>
      </form>
    );
  }
}